/*
 * Copyright (c) 2025 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { translationSchema } from '@well/translation';
import { TaskResourceService } from '@well/delivery-libs/task-management/resource-services';
import { BsDialogComponent, BsButtonDirective, BsButtonLabelDirective, BsTooltipDirective } from '@well/iriro-frontend-libs/bs-component-store';
import { MessageService } from 'primeng/api';
import { ErrorSeverityEnum } from '@irian-ro/shared-components-module';

@Component({
    selector: 'well-task-delete-dialog',
    templateUrl: './task-delete-dialog.component.html',
    imports: [BsDialogComponent, BsButtonDirective, BsButtonLabelDirective, TranslateModule, BsTooltipDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskDeleteDialogComponent {
  taskResourceService = inject(TaskResourceService);
  translateService = inject(TranslateService);
  messageService = inject(MessageService);

  deleted = output<void>();
  segmentDeleted = output<void>();
  solutionDeleted = output<void>();

  visible = signal(false);
  taskUuid = signal<string | null>(null);
  solutionsDeletable = signal(false);
  segmentsDeletable = signal(false);

  protected readonly translationSchema = translationSchema;

  open(uuid: string) {
    this.taskUuid.set(uuid);
    this.solutionsDeletable.set(false);
    this.segmentsDeletable.set(false);
    this.visible.set(true);
    this.taskResourceService.getDeleteState(uuid).subscribe((result) => {
      this.solutionsDeletable.set(result.solutionsDeletable);
      this.segmentsDeletable.set(result.segmentsDeletable);
    });
  }

  deleteTaskOnly() {
    if (!this.taskUuid()) return;
    this.visible.set(false);
    this.taskResourceService.delete(this.taskUuid()!).subscribe(() => {
      this.showDeleteSuccess();
    });
  }

  deleteSegment() {
    if (!this.taskUuid()) return;
    this.visible.set(false);
    this.taskResourceService.deleteSegment(this.taskUuid()!).subscribe(() => {
      this.showDeleteSuccess();
      this.segmentDeleted.emit();
    });
  }

  deleteTaskAndSolution() {
    if (!this.taskUuid()) return;
    this.visible.set(false);
    this.taskResourceService.deleteWithSolution(this.taskUuid()!).subscribe(() => {
      this.showDeleteWithSolutionSuccess();
    });
  }

  private showDeleteSuccess() {
    this.deleted.emit();
    this.messageService.add({
      key: 'global-notification',
      severity: ErrorSeverityEnum.success,
      summary: 'Success',
      life: 3000,
      detail: this.translateService.instant(translationSchema.task.list.deletedSuccessfully),
    });
  }

  private showDeleteWithSolutionSuccess() {
    this.solutionDeleted.emit();
    this.messageService.add({
      key: 'global-notification',
      severity: ErrorSeverityEnum.success,
      summary: 'Success',
      life: 3000,
      detail: this.translateService.instant(translationSchema.task.list.deletedSuccessfully),
    });
  }
}
